import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import fetch from 'isomorphic-unfetch'
import { setContext } from 'apollo-link-context'
import { SentryLink } from 'apollo-link-sentry'
import { IS_DEVELOPMENT, IS_STAGING } from 'utils/environments'
import { conversationsCacheConfig, interactionsCacheConfig } from './apolloCacheConfig'

const API_PATH = '/api/graphql'

function getServerGqlEndpoint(): string {
  if (process.browser) {
    return API_PATH
  }

  if (IS_DEVELOPMENT) {
    return `${process.env.CGC_WEB_CGC_APP_URL}${API_PATH}`
  }

  return `http://cgc-app:3000${API_PATH}`
}

function getHttpLink(
  domain: string,
  host: string,
  cookies: string,
  cloudflareForwardedIp?: string
) {
  const isServerSide = !process.browser
  const uri = getServerGqlEndpoint()
  const httpLink = createHttpLink({
    uri: uri,
    credentials: 'same-origin',
    fetch: isServerSide ? fetch : undefined,
  })

  if (!isServerSide && IS_DEVELOPMENT) {
    return httpLink
  }

  const headersLink = setContext((_, { headers }) => {
    headers = {
      ...headers,
      'x-cgc-domain': domain,
      'x-cgc-host': host,
    }

    if (cloudflareForwardedIp) {
      headers = {
        ...headers,
        'x-cgc-forwarded-ip': cloudflareForwardedIp,
      }
    }

    if (isServerSide) {
      headers = getHeadersWithAuhenticatedCookies(headers, cookies)
    }

    if (IS_STAGING) {
      headers = getHeaderswithBaiscAuth(headers)
    }

    return { headers: headers }
  })
  return headersLink.concat(httpLink as any)
}

const getHeaderswithBaiscAuth = <D>(headers: D): D & { authorization: string } => {
  const btoa = require('btoa')

  return {
    ...headers,
    authorization: 'Basic ' + btoa('careguidebeta:testdemo'),
  }
}

const getHeadersWithAuhenticatedCookies = <D>(
  headers: D,
  cookies: string
): D & { Cookies?: string } => {
  if (!cookies) {
    return headers
  }

  return {
    ...headers,
    Cookie: cookies,
  }
}

let apolloClient: ApolloClient<TODO> | null = null

function create<S>(
  initialState: S,
  domain: string,
  host: string,
  cookies: string,
  cloudflareForwardedIp: string
) {
  const httpLink = getHttpLink(domain, host, cookies, cloudflareForwardedIp)
  const sentryLinkOptions = {
    setTransaction: true,
    setFingerprint: true,
    attachBreadcrumbs: {
      includeQuery: true,
      includeVariables: false,
      includeResponse: false,
      includeError: true,
    },
  }
  return new ApolloClient({
    connectToDevTools: process.browser,
    ssrMode: !process.browser,
    link: ApolloLink.from([new SentryLink(sentryLinkOptions), httpLink] as any),
    cache: new InMemoryCache({
      typePolicies: {
        ...conversationsCacheConfig,
        ...interactionsCacheConfig,
      },
    }).restore(initialState || {}),
  })
}

export default function initApollo({
  initialState = null,
  domain,
  host,
  cookies = null,
  cloudflareForwardedIp,
}: {
  initialState?: object | null
  domain: string
  host: string
  cookies?: any
  cloudflareForwardedIp: string
}): ApolloClient<any> {
  if (!process.browser) {
    return create(initialState, domain, host, cookies, cloudflareForwardedIp)
  }

  if (!apolloClient) {
    apolloClient = create(initialState, domain, host, cookies, cloudflareForwardedIp)
  }

  return apolloClient
}
